"use strict";
/**
 * Разбор файла выгрузки при импорте.
 *
 * Схема описывает не «правильный JSON», а именно тот файл, который выдаёт
 * экспорт. Отсюда два свойства, которые важнее строгости:
 *
 *   1. Неизвестные поля не запрещаются. Выгрузка новой версии содержит
 *      больше, чем умеет прочитать старая; ронять импорт из-за поля, которое
 *      он всё равно не собирался использовать, — значит сделать формат
 *      одноразовым.
 *
 *   2. Поля, которые импорт не восстанавливает (проверки, версии активов),
 *      здесь не описаны вовсе. Описать их значило бы обещать, что они
 *      переживут перенос. Манифест вложений описан: содержимого в нём нет,
 *      но по контрольной сумме файл иногда удаётся найти в хранилище этой
 *      же установки — и тогда вложение восстанавливается.
 *
 * Сообщения об ошибках пишутся для человека, который открыл чужой файл и не
 * знает его устройства: «не похоже на выгрузку Project OS» полезнее, чем
 * «expected object, received undefined».
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.importProjectSchema = exports.projectExportFileSchema = exports.SUPPORTED_EXPORT_VERSION = void 0;
const contracts_1 = require("@project-os/contracts");
const zod_1 = require("zod");
/** Единственная версия формата, которую умеет читать этот импорт. */
exports.SUPPORTED_EXPORT_VERSION = 1;
const isoDateTime = zod_1.z
    .string()
    .refine((value) => !Number.isNaN(Date.parse(value)), 'Ожидается дата со временем');
const isoDay = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ожидается дата в формате ГГГГ-ММ-ДД');
/**
 * Идентификаторы внутри файла.
 *
 * Проверяются на непустоту, но не на формат UUID: они нужны только для того,
 * чтобы связать элементы между собой внутри одного файла, и заменяются на
 * новые при импорте. Требовать UUID значило бы отказываться от файлов,
 * собранных вручную или другой системой.
 */
const localId = zod_1.z.string().min(1, 'Пустой идентификатор');
const person = zod_1.z.object({
    name: zod_1.z.string().min(1),
    email: zod_1.z.string().min(3),
    role: zod_1.z.string().min(1),
    joinedAt: isoDateTime.optional(),
});
const workstream = zod_1.z.object({
    id: localId,
    name: zod_1.z.string().min(1, 'У направления нет названия'),
    descriptionMd: zod_1.z.string().nullish(),
    status: zod_1.z.string().min(1),
    owner: zod_1.z.string().nullish(),
});
const requirement = zod_1.z.object({
    id: localId,
    title: zod_1.z.string().min(1, 'У требования нет названия'),
    descriptionMd: zod_1.z.string().nullish(),
    status: zod_1.z.string().min(1),
    owner: zod_1.z.string().nullish(),
    itemIds: zod_1.z.array(localId).default([]),
});
const stage = zod_1.z.object({
    id: localId,
    name: zod_1.z.string().min(1, 'У этапа нет названия'),
    descriptionMd: zod_1.z.string().nullish(),
    status: zod_1.z.string().min(1),
    targetDate: isoDay.nullish(),
    completedAt: isoDateTime.nullish(),
    requirements: zod_1.z.array(requirement).default([]),
});
const expense = zod_1.z.object({
    amountMinor: zod_1.z.number().int(),
    currency: zod_1.z.string().min(3).max(3),
    category: zod_1.z.string().nullish(),
    vendor: zod_1.z.string().nullish(),
    expenseDate: isoDay,
    recurrence: zod_1.z.string().min(1),
    nextPaymentDate: isoDay.nullish(),
    paymentStatus: zod_1.z.string().nullish(),
});
const item = zod_1.z.object({
    id: localId,
    type: zod_1.z.string().min(1, 'У элемента не указан тип'),
    title: zod_1.z.string().min(1, 'У элемента нет названия'),
    bodyMd: zod_1.z.string().nullish(),
    status: zod_1.z.string().min(1, 'У элемента не указан статус'),
    priority: zod_1.z.string().nullish(),
    properties: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).default({}),
    expense: expense.nullish(),
    owner: zod_1.z.string().nullish(),
    createdBy: zod_1.z.string().nullish(),
    workstreamId: localId.nullish(),
    dueDate: isoDay.nullish(),
    isNextAction: zod_1.z.boolean().default(false),
    createdAt: isoDateTime,
    updatedAt: isoDateTime.optional(),
    completedAt: isoDateTime.nullish(),
    archivedAt: isoDateTime.nullish(),
});
const relation = zod_1.z.object({
    fromItemId: localId,
    relationType: zod_1.z.string().min(1),
    toItemId: localId,
    /**
     * Названия концов связи.
     *
     * При слиянии элемент из файла может уже существовать в проекте под другим
     * идентификатором — созданный здесь руками, а не импортом. Тогда связь
     * восстанавливается по названию: иначе она потерялась бы именно там, где
     * оба её конца на месте.
     */
    fromTitle: zod_1.z.string().nullish(),
    toTitle: zod_1.z.string().nullish(),
});
const attachment = zod_1.z.object({
    id: localId,
    itemId: localId.nullish(),
    filename: zod_1.z.string().min(1),
    mimeType: zod_1.z.string().min(1),
    sizeBytes: zod_1.z.number().int().nonnegative(),
    /** Только по ней и можно узнать тот же самый файл. */
    checksumSha256: zod_1.z.string().regex(/^[0-9a-f]{64}$/, 'Ожидается контрольная сумма SHA-256'),
    uploadedBy: zod_1.z.string().nullish(),
    createdAt: isoDateTime.optional(),
});
const comment = zod_1.z.object({
    itemId: localId,
    author: zod_1.z.string().nullish(),
    bodyMd: zod_1.z.string().min(1),
    createdAt: isoDateTime,
});
exports.projectExportFileSchema = zod_1.z.object({
    formatVersion: zod_1.z
        .number()
        .int()
        .refine((value) => value === exports.SUPPORTED_EXPORT_VERSION, `Файл сделан другой версией формата. Эта версия читает выгрузки формата ${exports.SUPPORTED_EXPORT_VERSION}.`),
    exportedAt: isoDateTime.optional(),
    project: zod_1.z.object({
        /**
         * Идентификатор исходного проекта.
         *
         * Нужен режимам обновления и слияния: при повторном импорте в ту же
         * установку он прямо указывает, какой проект имеется в виду. Название
         * для этого хуже — его переименовывают.
         */
        id: localId.optional(),
        name: zod_1.z.string().min(1, 'В файле нет названия проекта').max(240),
        descriptionMd: zod_1.z.string().nullish(),
        status: zod_1.z.string().min(1),
        currentGoal: zod_1.z.string().nullish(),
        startDate: isoDay.nullish(),
        targetDate: isoDay.nullish(),
        createdAt: isoDateTime.optional(),
        updatedAt: isoDateTime.optional(),
    }),
    members: zod_1.z.array(person).default([]),
    workstreams: zod_1.z.array(workstream).default([]),
    stages: zod_1.z.array(stage).default([]),
    items: zod_1.z.array(item).default([]),
    relations: zod_1.z.array(relation).default([]),
    comments: zod_1.z.array(comment).default([]),
    attachments: zod_1.z.array(attachment).default([]),
});
/**
 * Что и куда импортировать. Сам файл приходит отдельно, как файл.
 *
 * Режим по умолчанию — создание нового проекта. Так было до появления
 * остальных режимов, и так безопаснее всего: умолчание, способное изменить
 * существующий проект, однажды сработает случайно.
 *
 * Поля приходят из формы с файлом, то есть строками: числа и логические
 * значения в multipart не переживают дорогу.
 */
exports.importProjectSchema = zod_1.z.object({
    workspaceId: zod_1.z.uuid('Некорректный идентификатор'),
    mode: zod_1.z.enum(contracts_1.IMPORT_MODES).default('CREATE_NEW'),
    /**
     * Имя для нового проекта. Пусто — берётся из файла. Задаётся, когда проект
     * с таким названием уже есть и человек согласился на другое.
     */
    name: zod_1.z.string().trim().min(1).max(240).optional(),
    /**
     * Проект, в который импортировать. Только для обновления и слияния.
     *
     * Не обязателен: если не указан, проект ищется по идентификатору из файла,
     * а затем по точному совпадению названия. Указывают его тогда, когда
     * человек выбрал проект в списке — то есть в подавляющем большинстве
     * случаев.
     */
    targetProjectId: zod_1.z.uuid('Некорректный идентификатор проекта').optional(),
});
//# sourceMappingURL=project-import.js.map